<template>
  <div
    v-if="isExporting"
    class="flex flex-row items-center"
  >
    <ion-button
      :title="$t('hzba.export.abortExport')"
      @click.stop="abortExport"
    >
      <ion-icon
        slot="icon-only"
        :icon="closeCircleOutline"
        class="w-6 h-6"
      />
    </ion-button>
    <ion-spinner
      name="circular"
      class="ml-4"
    />
  </div>
  <div v-else-if="showAsStartButton">
    <ion-button
      title="GeoJSON Export"
      :disabled="isSelectionEmpty"
      @click.stop="exportGeojson"
    >
      <ion-icon
        slot="start"
        :icon="caretForwardOutline"
        class="w-6 h-6"
      />
      {{ $t('hzba.export.startExport') }}
    </ion-button>
  </div>

  <div v-else>
    <div
      v-if="onlyOneExportType"
      class="exportButton cursor-pointer"
    >
      <ion-button
        :title="title"
        @click.stop="onExport"
      >
        <ion-icon
          slot="icon-only"
          :icon="downloadOutline"
          class="w-6 h-6"
        />
      </ion-button>
    </div>
    <div v-else>
      <div class="exportButton cursor-pointer">
        <ion-button
          id="ms-export-button-popover-trigger"
          :title="title"
          @click="isPopoverOpen = true"
        >
          <ion-icon
            slot="icon-only"
            :icon="downloadOutline"
            class="w-6 h-6"
          />
        </ion-button>
      </div>
    </div>
  </div>
  <!-- adding trigger attribute too because otherwise popover is not displayed close to the button -->
  <!-- TODO: popover throws an console warning when button is not currently rendered -->
  <ion-popover
    ref="popoverElement"
    trigger="ms-export-button-popover-trigger"
    :is-open="isPopoverOpen"
    @did-dismiss="isPopoverOpen = false"
  >
    <ion-content>
      <ion-list class="p-0">
        <ion-item
          :button="true"
          :detail="false"
          @click.stop="exportExcel"
        >
          Excel
        </ion-item>
        <ion-item
          :button="true"
          :detail="false"
          @click.stop="emitExportGeojson"
        >
          GeoJSON
        </ion-item>
      </ion-list>
    </ion-content>
  </ion-popover>
</template>

<script
  setup
  lang="ts"
>
import { generateExcel } from "@/api/generate-excel-api";
import { useStore } from "@/composables/useTypedStore";
import useUser from "@/composables/useUser";
import BestandsaufnahmeModel from "@/models/ba/models/bestandsaufnahme.model";
import { SelectModeState } from "@/types/app/survey-types";
import { Monitoring } from "@/utilities/monitoring";
import { IonButton, IonContent, IonIcon, IonItem, IonList, IonPopover, IonSpinner } from "@ionic/vue";
import { Canceler } from "axios";
import { caretForwardOutline, closeCircleOutline, downloadOutline } from "ionicons/icons";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

withDefaults(
  defineProps<{
    showAsStartButton?: boolean;
  }>(),
  {
    showAsStartButton: false,
  }
);

const emit = defineEmits<{
  (e: "export-geojson"): void;
}>();

const isExporting = ref(false);
const abortExportFunction = ref<Canceler | null>(null);
const isPopoverOpen = ref(false);
const popoverElement = ref<InstanceType<typeof IonPopover> | null>(null);


const { user } = useUser();
const store = useStore();
const { t, locale } = useI18n({ useScope: "global" });

const isSelectionEmpty = computed(() => {
  if (store.state.entities.selectModeSurveyCards === SelectModeState.ALL) {
    return store.state.entities.bestandsaufnahmes.totalSearchResultsCount === 0;
  } else {
    return store.state.entities.bestandsaufnahmes.selectedSurveyCards.size === 0;
  }
});


const featureFlags = computed(() => user.value?.organisation?.featureFlags);
const currentProject = computed(() => store.state.user.currentUserProject);
const organisationPrefix = computed(() => store.getters["user/organisationPrefix"]);

const isGeoExportEnabled = computed(() => {
  return !!featureFlags.value?.survey?.exportgeojson;
});

const isExcelExportEnabled = computed(() => {
  return !!featureFlags.value?.survey?.exportxls;
});

const onlyOneExportType = computed(() => {
  return isGeoExportEnabled.value !== isExcelExportEnabled.value;
});

const title = computed(() => {
  if (onlyOneExportType.value) {
    return isGeoExportEnabled.value ? "Export GeoJSON" : "Export Excel";
  }
  return t("hzba.export.exportData");
});

const closePopover = () => {
  isPopoverOpen.value = false;
  popoverElement.value?.$el.dismiss(); // make sure to close the popover
};


const exportGeojson = () => {
  isExporting.value = true;
  closePopover();
  BestandsaufnahmeModel.dispatch("downloadGeodataForListOfSurveys")
    .finally(() => {
      isExporting.value = false;
    });
};

// TODO: move excel export logic to store similar to geojson export
const exportExcel = () => {
  if (user?.value?.organisation?.id && currentProject?.value?.id) {
    closePopover();
    isExporting.value = true;
    const { call, abort } = generateExcel(
      user.value.organisation.id,
      currentProject.value.id,
      t(`${organisationPrefix.value}hzba.bestandsaufnahmen`),
      locale.value
    );

    call.finally(() => {
      isExporting.value = false;
    });

    abortExportFunction.value = abort;
  } else {
    Monitoring.withScope((scope) => {
      scope.setContext("export info", { projectId: currentProject?.value?.id, organisationId: user?.value?.organisation?.id });
      Monitoring.error("Cannot export excel-sheet, no organisation or project id found for user");
    });
  }
};

const emitExportGeojson = () => {
  emit("export-geojson");
  closePopover();
};

const onExport = () => {
  if (isGeoExportEnabled.value) {
    emitExportGeojson();
  } else if (isExcelExportEnabled.value) {
    exportExcel();
  }
};

const abortExport = () => {
  if (abortExportFunction.value) {
    abortExportFunction.value();
  }
  BestandsaufnahmeModel.dispatch("abortExport");
};

</script>

<style
  scoped
  lang="scss"
>
ion-button {
  --background: transparent;
  --border-radius: 9999px;
  --box-shadow: 0 0 3px #ccc;
}
</style>
